

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./Tableordertype.css";

const CombinedDashboard = () => {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(null);
  const [activeTab, setActiveTab] = useState("confirmed");
  const [authValid, setAuthValid] = useState(true);

  // ✅ AUTH DATA WITH VALIDATION
  const getAuthData = useCallback(() => {
    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem("authToken");
    return { userId, token };
  }, []);

  // ✅ CHECK AUTHENTICATION STATUS
  const checkAuth = () => {
    const { userId, token } = getAuthData();
    const isValid = !!(userId && token);
    setAuthValid(isValid);
    if (!isValid) {
      setError("Session expired. Please login again.");
    }
    return isValid;
  };

  // ✅ FETCH TABLE DATA ONLY
  const fetchTableData = useCallback(async () => {
    const { userId, token } = getAuthData();
    const customerId = localStorage.getItem("customerId");

    if (!userId || !token || !customerId) {
      console.error("Missing userId, token, or customerId");
      return [];
    }

    try {
      const response = await fetch(
        `https://backend.maamaas.com/food/api/seatingdetails/get/by/tabledetails?userId=${userId}&customerId=${customerId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.error(err);
      return [];
    }
  }, [getAuthData]);

  // ✅ FETCH ALL DATA
  const fetchAllData = async () => {
    setLoading(true);
    setError(null);

    if (!checkAuth()) {
      setLoading(false);
      return;
    }

    try {
      const tables = await fetchTableData();
      setTableData(tables);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Fetch error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ MARK ARRIVED - Only marks as arrived, shows Add Item button
  const handleMarkArrived = async (item) => {
    const { token } = getAuthData();

    if (!token) {
      showToast("Session expired. Please login again.", "error");
      return;
    }

    setActionInProgress(item.id);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const updatedBooking = {
        id: item.id,
        userId: item.userId,
        startTime: item.startTime,
        durationMinutes: item.durationMinutes,
        phoneNumber: item.phoneNumber,
        guestName: item.guestName,
        bookingDate: item.bookingDate,
        arrivalStatus: "ARRIVED",
        types: item.types,
        capacity: item.capacity,
        seating: item.seating,
        seatingId: item.seatingId,
        vendorId: item.vendorId,
        code: item.code
      };

      const response = await fetch(
        `https://backend.maamaas.com/food/api/seatingdetails/seating-details/${item.id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedBooking),
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem("authToken");
        localStorage.removeItem("userId");
        showToast("Session expired. Please login again.", "error");
        setAuthValid(false);
        return;
      }

      if (response.ok) {
        await fetchAllData();
        showToast(`Table ${item.code} marked as Arrived ✅`, "success");
      } else {
        showToast(`Failed to update: ${response.status}`, "error");
      }
    } catch (err) {
      console.error("Mark arrived error:", err);
      showToast(`Failed to update: ${err.message}`, "error");
    } finally {
      setActionInProgress(null);
    }
  };

  // ✅ MARK ORDER AS COMPLETED
  const handleCompleteOrder = async (item) => {
    const { token } = getAuthData();

    if (!token) {
      showToast("Session expired. Please login again.", "error");
      return;
    }

    setActionInProgress(item.id);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const updatedBooking = {
        id: item.id,
        userId: item.userId,
        startTime: item.startTime,
        durationMinutes: item.durationMinutes,
        phoneNumber: item.phoneNumber,
        guestName: item.guestName,
        bookingDate: item.bookingDate,
        arrivalStatus: "COMPLETED",
        types: item.types,
        capacity: item.capacity,
        seating: item.seating,
        seatingId: item.seatingId,
        vendorId: item.vendorId,
        code: item.code
      };

      const response = await fetch(
        `https://backend.maamaas.com/food/api/seatingdetails/seating-details/${item.id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedBooking),
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem("authToken");
        localStorage.removeItem("userId");
        showToast("Session expired. Please login again.", "error");
        setAuthValid(false);
        return;
      }

      if (response.ok) {
        await fetchAllData();
        showToast(`Table ${item.code} order completed successfully ✅`, "success");
        setActiveTab("completed");
      } else {
        showToast(`Failed to complete order: ${response.status}`, "error");
      }
    } catch (err) {
      console.error("Complete order error:", err);
      showToast(`Failed to complete: ${err.message}`, "error");
    } finally {
      setActionInProgress(null);
    }
  };

  // ✅ ADD ITEM HANDLER - Navigates to menu
  const handleAddItem = async (item) => {
    const { token, userId } = getAuthData();

    if (!token) {
      showToast("Session expired. Please login again.", "error");
      return;
    }

    setActionInProgress(item.id);

    try {
      const vendorId = item.vendorId || 98;
      
      navigate(`/MenuitemsTablemain/${vendorId}`, {
        state: {
          seatingId: item.id,
          tableCode: item.code,
          guestName: item.guestName,
          bookingId: item.id,
          userId: userId,
          bookingDate: item.bookingDate,
          startTime: item.startTime
        }
      });
      
      showToast(`Adding items for ${item.guestName || "guest"}...`, "info");
    } catch (err) {
      console.error("Add item error:", err);
      const vendorId = item.vendorId || 98;
      navigate(`/MenuitemsTablemain/${vendorId}`, {
        state: {
          seatingId: item.seatingId,
          tableCode: item.code,
          guestName: item.guestName,
          bookingId: item.id,
          userId: getAuthData().userId
        }
      });
      showToast("Redirecting to menu...", "info");
    } finally {
      setActionInProgress(null);
    }
  };

  // ✅ TOAST NOTIFICATION SYSTEM
  const showToast = (message, type = "info") => {
    const toast = document.createElement("div");
    toast.className = `cd-toast cd-toast-${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.classList.add("cd-toast-show");
      setTimeout(() => {
        toast.classList.remove("cd-toast-show");
        setTimeout(() => document.body.removeChild(toast), 300);
      }, 2700);
    }, 10);
  };

  // ✅ LOGOUT HANDLER
  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userId");
    setAuthValid(false);
    setError("Please login again to continue.");
    setTableData([]);
    showToast("Logged out successfully", "info");
    navigate("/login");
  };

  // ✅ STATUS BADGES
  const getStatusBadge = (arrivalStatus) => {
    const statusMap = {
      CONFIRMED: { label: "Confirmed", class: "cd-badge-confirmed" },
      NOT_ARRIVED: { label: "Confirmed", class: "cd-badge-confirmed" },
      ARRIVED: { label: "Arrived", class: "cd-badge-arrived" },
      CANCELLED: { label: "Cancelled", class: "cd-badge-cancelled" },
      COMPLETED: { label: "Completed", class: "cd-badge-completed" },
    };
    const status = statusMap[arrivalStatus] || { label: arrivalStatus || "Pending", class: "cd-badge-default" };
    return <span className={`cd-status-badge ${status.class}`}>{status.label}</span>;
  };

  // ✅ FORMAT FUNCTIONS
  const formatTime = (time) => {
    if (!time) return "--";
    const [h, m] = time.split(":");
    const hour = parseInt(h);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${m} ${ampm}`;
  };

  const formatDate = (date) => {
    if (!date) return "--";
    const d = new Date(date);
    return d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = () => {
    if (!lastUpdated) return "";
    return lastUpdated.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // ✅ SORTED DATA
  const sortedTableData = [...tableData].sort((a, b) => {
    const dateCompare = b.bookingDate?.localeCompare(a.bookingDate || "") || 0;
    if (dateCompare !== 0) return dateCompare;
    return (b.startTime || "").localeCompare(a.startTime || "");
  });

  // ✅ FILTER BOOKINGS - Only show booked tables
  const confirmedBookings = sortedTableData.filter(
    item => item.arrivalStatus === "CONFIRMED" || item.arrivalStatus === "NOT_ARRIVED"
  );

  const arrivedBookings = sortedTableData.filter(
    item => item.arrivalStatus === "ARRIVED"
  );

  const completedBookings = sortedTableData.filter(
    item => item.arrivalStatus === "COMPLETED"
  );

  // ✅ AUTO REFRESH
  useEffect(() => {
    if (authValid) {
      fetchAllData();
      const interval = setInterval(fetchAllData, 30000);
      return () => clearInterval(interval);
    }
  }, [authValid]);

  // ✅ INITIAL AUTH CHECK
  useEffect(() => {
    checkAuth();
  }, []);

  // ✅ COUNTS
  const confirmedCount = confirmedBookings.length;
  const arrivedCount = arrivedBookings.length;
  const completedCount = completedBookings.length;
  const totalBookings = confirmedCount + arrivedCount + completedCount;

  return (
    <div className="cd-container">
      {/* Premium Header */}
      <div className="cd-header">
        <div className="cd-header-left">
          <div className="cd-logo-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div>
            <h1 className="cd-title">My Table Bookings</h1>
            <p className="cd-header-subtitle">Track your table reservations and orders</p>
          </div>
        </div>
        <div className="cd-header-right">
          <button onClick={fetchAllData} className="cd-refresh-btn" disabled={loading}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 4v6h-6M1 20v-6h6" stroke="currentColor" strokeLinecap="round"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" stroke="currentColor" strokeLinecap="round"/>
            </svg>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
          {!authValid && (
            <button onClick={handleLogout} className="cd-logout-btn">
              Login Again
            </button>
          )}
          {lastUpdated && totalBookings > 0 && (
            <div className="cd-last-updated">
              <span className="cd-dot"></span>
              Updated {formatDateTime()}
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards - Only show if there are bookings */}
      {!loading && !error && totalBookings > 0 && (
        <div className="cd-stats-grid">
          <div className="cd-stat-card cd-stat-confirmed">
            <div className="cd-stat-value">{confirmedCount}</div>
            <div className="cd-stat-label">Booked</div>
          </div>
          <div className="cd-stat-card cd-stat-arrived">
            <div className="cd-stat-value">{arrivedCount}</div>
            <div className="cd-stat-label">Arrived</div>
          </div>
          <div className="cd-stat-card cd-stat-completed">
            <div className="cd-stat-value">{completedCount}</div>
            <div className="cd-stat-label">Completed</div>
          </div>
        </div>
      )}

      {/* Tab Navigation - Only show relevant tabs */}
      <div className="cd-tabs">
        <button
          className={`cd-tab ${activeTab === "confirmed" ? "cd-tab-active" : ""}`}
          onClick={() => setActiveTab("confirmed")}
        >
          <span className="cd-tab-icon">✓</span>
          Booked
          {confirmedCount > 0 && <span className="cd-tab-badge">{confirmedCount}</span>}
        </button>
        <button
          className={`cd-tab ${activeTab === "completed" ? "cd-tab-active" : ""}`}
          onClick={() => setActiveTab("completed")}
        >
          <span className="cd-tab-icon">✔</span>
          Completed
          {completedCount > 0 && <span className="cd-tab-badge">{completedCount}</span>}
        </button>
      </div>

      {/* Auth Warning Banner */}
      {!authValid && (
        <div className="cd-auth-warning">
          <span>⚠️</span>
          <p>Your session has expired. Please login again to continue.</p>
          <button onClick={handleLogout}>Login</button>
        </div>
      )}

      {/* Main Content */}
      {loading && tableData.length === 0 ? (
        <div className="cd-loading-state">
          <div className="cd-spinner"></div>
          <p>Loading your bookings...</p>
        </div>
      ) : error ? (
        <div className="cd-error-state">
          <div className="cd-error-icon">⚠️</div>
          <h3>Unable to load data</h3>
          <p>{error}</p>
          <button onClick={fetchAllData} className="cd-retry-btn">Try Again</button>
        </div>
      ) : totalBookings === 0 ? (
        <div className="cd-empty-state">
          <div className="cd-empty-icon">📅</div>
          <h3>No Bookings Found</h3>
          <p>You haven't made any table reservations yet.</p>
          
        </div>
      ) : activeTab === "confirmed" ? (
        // CONFIRMED SECTION (Confirmed + Arrived)
        confirmedBookings.length === 0 && arrivedBookings.length === 0 ? (
          <div className="cd-empty-state">
            <div className="cd-empty-icon">📌</div>
            <h3>No confirmed bookings</h3>
            <p>Your confirmed bookings will appear here</p>
          </div>
        ) : (
          <div className="cd-cards-grid">
            {/* Show confirmed bookings (NOT_ARRIVED/CONFIRMED) */}
            {confirmedBookings.map((item) => (
              <div key={item.id} className="cd-card cd-card-confirmed">
                <div className="cd-card-header">
                  <div className="cd-card-code">Table {item.code}</div>
                  {getStatusBadge(item.arrivalStatus)}
                </div>

                <div className="cd-card-section">
                  <div className="cd-card-field">
                    <span className="cd-field-icon">👤</span>
                    <span className="cd-field-value">{item.guestName || "—"}</span>
                  </div>
                  <div className="cd-card-field">
                    <span className="cd-field-icon">📞</span>
                    <span className="cd-field-value">{item.phoneNumber || "—"}</span>
                  </div>
                </div>

                <div className="cd-card-section cd-card-details">
                  <div className="cd-card-field">
                    <span className="cd-field-icon">📅</span>
                    <span className="cd-field-label">Date</span>
                    <span className="cd-field-value">{formatDate(item.bookingDate)}</span>
                  </div>
                  <div className="cd-card-field">
                    <span className="cd-field-icon">⏰</span>
                    <span className="cd-field-label">Time</span>
                    <span className="cd-field-value">{formatTime(item.startTime)}</span>
                  </div>
                  <div className="cd-card-field">
                    <span className="cd-field-icon">👥</span>
                    <span className="cd-field-label">Guests</span>
                    <span className="cd-field-value">{item.capacity}</span>
                  </div>
                </div>

                <div className="cd-card-actions">
                  <button
                    className="cd-btn cd-btn-success"
                    onClick={() => handleMarkArrived(item)}
                    disabled={actionInProgress === item.id}
                  >
                    {actionInProgress === item.id ? (
                      <span className="cd-btn-spinner"></span>
                    ) : (
                      <>✓ Mark Arrived</>
                    )}
                  </button>
                </div>
              </div>
            ))}

            {/* Show arrived bookings (ARRIVED) */}
            {arrivedBookings.map((item) => (
              <div key={item.id} className="cd-card cd-card-arrived">
                <div className="cd-card-header">
                  <div className="cd-card-code">Table {item.code}</div>
                  {getStatusBadge(item.arrivalStatus)}
                </div>

                <div className="cd-card-section">
                  <div className="cd-card-field">
                    <span className="cd-field-icon">👤</span>
                    <span className="cd-field-value">{item.guestName || "—"}</span>
                  </div>
                  <div className="cd-card-field">
                    <span className="cd-field-icon">📞</span>
                    <span className="cd-field-value">{item.phoneNumber || "—"}</span>
                  </div>
                </div>

                <div className="cd-card-section cd-card-details">
                  <div className="cd-card-field">
                    <span className="cd-field-icon">📅</span>
                    <span className="cd-field-label">Date</span>
                    <span className="cd-field-value">{formatDate(item.bookingDate)}</span>
                  </div>
                  <div className="cd-card-field">
                    <span className="cd-field-icon">⏰</span>
                    <span className="cd-field-label">Time</span>
                    <span className="cd-field-value">{formatTime(item.startTime)}</span>
                  </div>
                  <div className="cd-card-field">
                    <span className="cd-field-icon">👥</span>
                    <span className="cd-field-label">Guests</span>
                    <span className="cd-field-value">{item.capacity}</span>
                  </div>
                </div>

                <div className="cd-card-actions">
                  <button
                    className="cd-btn cd-btn-primary"
                    onClick={() => handleAddItem(item)}
                    disabled={actionInProgress === item.id}
                  >
                    {actionInProgress === item.id ? (
                      <span className="cd-btn-spinner"></span>
                    ) : (
                      <>🍽️ Add Item</>
                    )}
                  </button>
                  <button
                    className="cd-btn cd-btn-success"
                    onClick={() => handleCompleteOrder(item)}
                    disabled={actionInProgress === item.id}
                  >
                    {actionInProgress === item.id ? (
                      <span className="cd-btn-spinner"></span>
                    ) : (
                      <>✅ Complete Order</>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        // COMPLETED SECTION
        completedBookings.length === 0 ? (
          <div className="cd-empty-state">
            <div className="cd-empty-icon">🏆</div>
            <h3>No completed bookings</h3>
            <p>Completed bookings will appear here after you complete the order</p>
          </div>
        ) : (
          <div className="cd-cards-grid">
            {completedBookings.map((item) => (
              <div key={item.id} className="cd-card cd-card-completed">
                <div className="cd-card-header">
                  <div className="cd-card-code">Table {item.code}</div>
                  {getStatusBadge(item.arrivalStatus)}
                </div>

                <div className="cd-card-section">
                  <div className="cd-card-field">
                    <span className="cd-field-icon">👤</span>
                    <span className="cd-field-value">{item.guestName || "—"}</span>
                  </div>
                  <div className="cd-card-field">
                    <span className="cd-field-icon">📞</span>
                    <span className="cd-field-value">{item.phoneNumber || "—"}</span>
                  </div>
                </div>

                <div className="cd-card-section cd-card-details">
                  <div className="cd-card-field">
                    <span className="cd-field-icon">📅</span>
                    <span className="cd-field-label">Date</span>
                    <span className="cd-field-value">{formatDate(item.bookingDate)}</span>
                  </div>
                  <div className="cd-card-field">
                    <span className="cd-field-icon">⏰</span>
                    <span className="cd-field-label">Time</span>
                    <span className="cd-field-value">{formatTime(item.startTime)}</span>
                  </div>
                  <div className="cd-card-field">
                    <span className="cd-field-icon">👥</span>
                    <span className="cd-field-label">Guests</span>
                    <span className="cd-field-value">{item.capacity}</span>
                  </div>
                </div>

                <div className="cd-card-completed-badge">
                  <span className="cd-completed-stamp">✓ ORDER COMPLETED ✓</span>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default CombinedDashboard;